import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { widgetFormSchema } from "../../lib/validators";
import { Input } from "../ui/Input";
import { Label } from "../ui/Label";
import { Textarea } from "../ui/Textarea";
import { Select } from "../ui/Select";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";

export type WidgetFormValues = z.infer<typeof widgetFormSchema>;

interface WidgetFormProps {
  defaultValues?: Partial<WidgetFormValues>;
  onSubmit: (values: WidgetFormValues) => Promise<void>;
  mode: "create" | "update";
}

export const WidgetForm = ({ defaultValues, onSubmit, mode }: WidgetFormProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<WidgetFormValues>({
    resolver: zodResolver(widgetFormSchema),
    defaultValues
  });

  const handleFormSubmit = async (values: WidgetFormValues) => {
    await onSubmit(values);
    if (mode === "create") {
      reset({ name: "", description: "", status: "draft" });
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="space-y-2">
        <Label htmlFor="widget-name">Widget name</Label>
        <Input id="widget-name" placeholder="Website Messenger" {...register("name")} />
        {errors.name ? <p className="text-xs text-red-400">{errors.name.message}</p> : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor="widget-description">Description</Label>
        <Textarea
          id="widget-description"
          rows={3}
          placeholder="Short description to identify this widget"
          {...register("description")}
        />
        {errors.description ? (
          <p className="text-xs text-red-400">{errors.description.message}</p>
        ) : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor="widget-status">Status</Label>
        <Select id="widget-status" defaultValue={defaultValues?.status ?? "draft"} {...register("status")}>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="archived">Archived</option>
        </Select>
      </div>
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <Spinner size="sm" /> Saving
          </>
        ) : mode === "create" ? (
          "Create widget"
        ) : (
          "Update widget"
        )}
      </Button>
    </form>
  );
};
